/**
 * Money in and out of the screen — formatting and input normalisation ONLY.
 *
 * The client never does arithmetic on money: every total comes from SQL or from
 * packages/analytics (DBD-014, react-pwa-engineering skill 6.1). What lives here is the
 * two edges of that rule — turning what the user typed into an integer VND amount, and
 * turning an amount from the database into something a person reads.
 *
 * VND has no minor unit (ISO 4217 exponent 0), which is the only reason an amount is a
 * plain integer. Supporting a currency with cents later is a deliberate migration, not an
 * assumption to build on.
 */
const VND_FORMATTER = new Intl.NumberFormat('vi-VN', {
  style: 'currency',
  currency: 'VND',
  maximumFractionDigits: 0,
});

const PLAIN_FORMATTER = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 0 });

/** "12.500.000 ₫" — for anything the user reads as an amount of money. */
export function formatVnd(amount: number): string {
  return VND_FORMATTER.format(amount);
}

/** "12.500.000" — grouped digits without the symbol, for input fields and tables. */
export function formatVndPlain(amount: number): string {
  return PLAIN_FORMATTER.format(amount);
}

export type TypedAmount = Readonly<{
  /** null while the field is empty — which is not the same as zero. */
  amount: number | null;
  /** What the field shows back: the same digits, regrouped. */
  display: string;
}>;

/**
 * Reads an amount as the user types it.
 *
 * Everything that is not a digit is dropped, including separators and a minus sign. That is
 * deliberate: a decimal comma in a VND field ("120,50") means the person slipped, and
 * *interpreting* it would silently change the amount by a factor of a hundred — in a
 * ledger. Digits only means what you see is what gets posted. Debt is entered as a positive
 * amount on a liability account, so a minus sign has nothing to express either.
 *
 * Returns null for an amount beyond the safe integer range: a number that cannot be
 * represented exactly must never reach the ledger.
 */
export function readTypedAmount(raw: string): TypedAmount {
  const digits = raw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  if (digits === '') return { amount: null, display: '' };

  const amount = Number(digits);
  if (!Number.isSafeInteger(amount)) return { amount: null, display: '' };

  return { amount, display: formatVndPlain(amount) };
}
